import React, {useEffect, useRef, useState, createRef} from "react";
import defaultStyles from "./Select.module.scss";
import {useTranslation} from "react-i18next";
import Checkbox from "../Checkbox/Checkbox";
import RadioButton from "../RadioButton/RadioButton";

export type SelectOptionType = {text: string | React.ReactNode; value: string | number | undefined};

type SelectProps = {
  value?: string | number | (string | number)[];
  onChange?: (value: string | number | (string | number)[] | undefined) => void;
  placeHolder?: string | React.ReactNode;
  options: SelectOptionType[];
  loading?: boolean;
  title?: string | React.ReactNode;
  multi?: boolean;
  styles?: SelectClasses;
  showCheckboxes?: true;
  showRadioButtons?: boolean;
};

type SelectClasses = {
  wrapper?: string;
  optionsWrapper?: string;
  selected?: string;
  option?: string;
  closeArrow?: string;
  openArrow?: string;
  placeholder?: string;
};

const Select: React.FC<SelectProps> = ({
  onChange = () => {
    return;
  },
  placeHolder,
  options,
  value,
  title = "",
  multi = false,
  styles = {wrapper: "", optionsWrapper: "", option: "", selected: "", closeArrow: "", openArrow: "", placeholder: ""},
  showCheckboxes,
  showRadioButtons = false,
}) => {
  const {t} = useTranslation();

  const [selectedOptions, setSelectedOptions] = useState<SelectOptionType[]>([]);
  const [opened, setOpened] = useState(false);
  const [markedValue, setMarkedValue] = useState("");

  const containerRef = useRef<HTMLDivElement>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const optionsRefs = useRef(Array.from({length: options.length}, () => createRef<HTMLDivElement>()));
  const openRef = useRef(false);
  const searchWord = useRef("");
  const last = useRef(new Date().getTime());

  useEffect(() => {
    const handleClick = (evt: MouseEvent) => {
      if (!openRef.current) {
        return;
      }
      if (!(containerRef.current === evt.target || containerRef.current?.contains(evt.target as HTMLElement))) {
        setOpened(false);
        openRef.current = false;
        setMarkedValue("");
      }
    };

    document.body.addEventListener("click", handleClick);

    return () => {
      document.body.removeEventListener("click", handleClick);
    };
  }, []);

  useEffect(() => {
    if (Array.isArray(value)) {
      setSelectedOptions(options.filter((option) => option.value && value.includes(option.value)));
    } else {
      const option = options.find((_option) => _option.value === value);

      setSelectedOptions(option ? [option] : []);
    }
  }, [value, options]);

  // option click
  const onSelect = (option: SelectOptionType) => {
    onChange(option.value);
    if (!multi) {
      setOpened(false);
      openRef.current = false;
      setMarkedValue("");
    }
  };

  const handleKeyUpEvent = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (openRef.current) {
      const now = new Date().getTime();
      if (now - last.current < 2000) {
        searchWord.current += event.key;
      } else {
        searchWord.current = event.key;
        setMarkedValue("");
      }

      last.current = now;

      const optionIndex = optionsRefs.current.findIndex((_option) =>
        _option.current?.innerText.toLowerCase().startsWith(searchWord.current.toLowerCase()),
      );
      if (optionIndex > -1) {
        const option = optionsRefs.current[optionIndex].current;
        if (option) {
          optionsContainerRef.current?.scrollTo({
            top: option.offsetTop - optionsContainerRef.current.offsetHeight / 2 + option.offsetHeight,
          });
          setMarkedValue(String(options[optionIndex].value));
        }
      } else {
        const indexes: number[] = [];
        optionsRefs.current.forEach((_option, index) => {
          if (
            String(_option.current?.innerText)
              .toLowerCase()
              .startsWith(searchWord.current[searchWord.current.length - 1].toLowerCase())
          ) {
            indexes.push(index);
          }
        });

        if (indexes.length) {
          const index =
            searchWord.current.length % indexes.length === 0
              ? indexes.length - 1
              : (searchWord.current.length % indexes.length) - 1;
          const option = optionsRefs.current[indexes[index]].current;
          if (option) {
            optionsContainerRef.current?.scrollTo({
              top: option.offsetTop - optionsContainerRef.current.offsetHeight / 2 + option.offsetHeight,
            });
            setMarkedValue(String(options[indexes[index]].value));
          }
        }
      }
    }
  };

  // is there an option, that can be cleared by 'onClear'
  const isOneOrLessOption = options.length <= 1;

  // styles handling design and behaviour
  const containerClassName = [
    defaultStyles.wrapper,
    isOneOrLessOption ? defaultStyles.unselectable : "",
    styles.wrapper,
    styles.closeArrow,
    opened ? styles.openArrow : "",
  ].join(" ");

  return (
    <div
      ref={containerRef}
      className={containerClassName}
      onKeyUp={handleKeyUpEvent}
      tabIndex={0}
      onClick={() => {
        setOpened(!opened);
        openRef.current = !opened;
        setMarkedValue("");
      }}
    >
      <span className={"custom-arrow-background"} />
      <span />
      {selectedOptions.length ? (
        <>
          {title} {multi ? "" : selectedOptions[0].text}
        </>
      ) : (
        <span className={[defaultStyles.placeholder, styles.placeholder].join(" ")}>
          {placeHolder ?? t("form.pleaseChoose")}
        </span>
      )}
      {opened && options.length > 0 && (
        <div ref={optionsContainerRef} className={`${defaultStyles.optionsWrapper}  ${styles.optionsWrapper ?? ""}`}>
          {options.map((option, index) => (
            <div
              ref={optionsRefs.current[index]}
              key={index}
              className={`${defaultStyles.option} ${markedValue === String(option.value) ? defaultStyles.marked : ""} ${
                multi || showCheckboxes ? defaultStyles.multi : ""
              } ${styles.option ?? ""} ${
                selectedOptions.some((_option) => _option.value === option.value)
                  ? [defaultStyles.selected, styles.selected].join(" ")
                  : ""
              }`}
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              {multi || showCheckboxes ? (
                <Checkbox
                  className={defaultStyles.checkbox}
                  wrapperClassName={defaultStyles.checkboxWrapper}
                  checked={selectedOptions.some((_option) => _option.value === option.value)}
                  color={"green"}
                  onChange={() => {
                    onSelect(option);
                  }}
                >
                  {option.text}
                </Checkbox>
              ) : showRadioButtons ? (
                <RadioButton
                  value={option.value}
                  checked={selectedOptions.some((_option) => _option.value === option.value)}
                  name={"percentageDiscount"}
                  onChange={() => {
                    onSelect(option);
                  }}
                >
                  {option.text}
                </RadioButton>
              ) : (
                <div
                  onClick={() => {
                    onSelect(option);
                  }}
                >
                  {option.text}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Select;
